import { ReactElement, useContext, useEffect, useState } from "react";
import InterruptionChart from "./interruptionChart";
import InterruptionTable from "./interruptionTable";
import FilterComponentBase from "../../components/filterComponentBase";
import { IDelayBase, IDelayMeltShop, IFilterData, IState, ITreeCategoryNode, NullValue } from "../../types";
import * as d3 from "d3";
import { ISeries, IPieConfig } from "../../components/charts/types";
import { getPalette } from "../../configuration/configuration";
import { List } from "linqts";
import { useDispatch, useSelector } from "react-redux";
import { addAlert } from "../../actions";
import { DelayContext } from "../../context/delayContext";
import { useFetch } from "../../system/useFetch";
import PieChart from "../../components/charts/pieChart";
import { FormControl, InputLabel, MenuItem, Select, Alert, Paper, IconButton } from "@mui/material";
import { getCurrentDatetimeInSeconds } from "../../system/utils";
import SkeletonView from "../../components/skeletonView";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DateTime } from "luxon";
import useDimensions from "../../system/useDimensions";

interface IDelayAcum {
  code: string;
  color: string | null;
  count: number;
  total: number;
}

interface IInterruptionPageProps {
  parentSize: {
    width: number;
    height: number;
  };
}

const divIndicator = {
  width: 400,
  margin: "0.5em 2.5em 0.5em 0.5em"
};

const divIndicatorContainer = {
  height: 200,
  margin: "0.5em 0.5em 0.5em 0.5em"
};


const InterruptionPage = ({ parentSize }: IInterruptionPageProps): ReactElement => {
  const dispatch = useDispatch<any>();
  // const systemContext = useContext(SystemContext);
  const delayContext = useContext(DelayContext);
  const selectedNode = useSelector((state: IState) => state.selectedNode);
  const filter = useSelector<IState, IFilterData>(state => state.filter);
  const [selectedMachine, setSelectedMachine] = useState<string | null>(selectedNode.children ? selectedNode.children[0].code : null);
  const [open, setOpen] = useState(false);
  const { loading, error, data } = useFetch<IDelayMeltShop[]>({ module: "delay", query: "getDelaysByFilter", payload: { ...filter.remote } });
  const [selected, setSelected] = useState<IDelayBase | null>(null);
  const [delays, setDelays] = useState<IDelayMeltShop[]>([]);
  const [ref, dimensions] = useDimensions<HTMLDivElement>();

  const isEditable = true;

  const [pieConfig, setPieConfig] = useState<IPieConfig>({
    width: 200,
    maximum: 1000,
    padAngle: 0.05,
    innerRadius: 0.2,
    seriesRadius: 1.1,
    showLegend: false,
    emptyColor: getPalette().statusColor.production.background,
    title: "Total Delays",
    sort: "NONE",
    series: [],
    formatValue: n => n.toFixed(1)
  });

  const [pieConfigMinutes, setPieConfigMinutes] = useState<IPieConfig>({
    width: 200,
    maximum: 1000,
    padAngle: 0.05,
    innerRadius: 0.2,
    seriesRadius: 1.1,
    showLegend: false,
    title: "Minutes x Category",
    sort: "NONE",
    series: []
  });

  const [pieConfigOccurrences, setPieConfigOccurrences] = useState<IPieConfig>({
    width: 200,
    maximum: 1000,
    padAngle: 0.05,
    innerRadius: 0.2,
    seriesRadius: 1.1,
    showLegend: false,
    title: "Occurrences x Category",
    sort: "NONE",
    series: [],
    formatValue: (n) => n.toFixed(0)
  });

  useEffect(() => {
    if (!data) {
      setDelays([]);

      return;
    }

    const delaysData: IDelayMeltShop[] = selectedMachine ? data.filter(x => !selectedMachine || x.production?.machine.code === selectedMachine) : data;
    setDelays(delaysData);

  }, [data, selectedMachine]);


  useEffect(() => {
    let secondsInSelectedDay;

    if (filter.remote.factoryDay) {
      secondsInSelectedDay = 60 * 24;
    } else {
      secondsInSelectedDay = Math.floor((DateTime.fromISO(filter.local.selectedTo).toSeconds() - DateTime.fromISO(filter.local.selectedFrom).toSeconds()) / 60);
    }

    const sumDelaysTime = delays.filter(x => !x.isDownDay).reduce((sum, current) => sum + ((current.endDatetime === null ? getCurrentDatetimeInSeconds() : current.endDatetime) - current.beginDatetime), 0);
    const sumDelaysTimeSch = delays.filter(x => x.isDownDay).reduce((sum, current) => sum + ((current.endDatetime === null ? getCurrentDatetimeInSeconds() : current.endDatetime) - current.beginDatetime), 0);
    // const total = Math.round(sumDelaysTime * 100 / secondsInSelectedDay);

    const newPieConfig = { ...pieConfig };
    newPieConfig.maximum = secondsInSelectedDay;
    newPieConfig.series = [
      { name: "Delays", value:  sumDelaysTime / 60, color: getPalette().statusColor.error.background, unit: "min" }
    ];

    if (sumDelaysTimeSch / 60 > 0) {
      newPieConfig.series.push({ name: "Scheduled", value: sumDelaysTimeSch / 60, color: getPalette().statusColor.scheduleDay.background, unit: "min" });
    }

    setPieConfig(newPieConfig);

    // endSection

    const delaysAcum: IDelayAcum[] = [];

    delays.forEach(delay => {
      if (!delay.isDownDay) {
        const exist = delaysAcum.find((d) => d.code === (delay.category?.name ?? "UNDEFINED"));

        if (exist) {
          exist.count += 1;
          exist.total += delay.endDatetime ? (delay.endDatetime - delay.beginDatetime) / 60 : 0;
        } else {
          delaysAcum.push({
            color: delay.category?.color ?? "#5f5f5",
            code: delay.category?.name ?? "UNDEFINED",
            count: 1,
            total: delay.endDatetime ? (delay.endDatetime - delay.beginDatetime) / 60 : 0
          });
        }
      }
    });

    const barSeries: ISeries[] = [];
    const barAcumSeries: ISeries[] = [];

    const listDelays = new List<IDelayAcum>(delaysAcum);

    // const fillSeries = (seriesList: ISeries[]) => {
    //   const pending = 5 - seriesList.length;
    //   for (let seriesI = 0; seriesI < pending; seriesI++) {
    //     seriesList.push({ name: `hidden-${seriesI}`, value: 0, color: "#fff", hidden: true });
    //   }
    // };

    // pido las 5 demoras ordenadas por tiempo
    //  const seriesOrderByTotalTime = new List<IDelayAcum>(delaysAcum).OrderByDescending(s=> s.total).Take(5).ToList().Select(x=> new ISeries {name: x.code, value: x.total, color: "#3e8ef7"});
    //  const lstOrderByTotalTime = new List<IDelayAcum>(delaysAcum).OrderByDescending(s=> s.total).Take(5).ToList();
    listDelays.OrderByDescending(s => s.total).Take(5).Select((y, index) => {
      barSeries.push({ name: y.code, value: y.total, color: y.color ?? getPalette().list[index], unit: "min"  });
    });

    // fillSeries(barSeries);


    // pido las 5 demoras ordenadas por cantidad de veces que aparecieron
    //  const seriesOrderByTotalVoices = new List<IDelayAcum>(delaysAcum).OrderByDescending(s=> s.total).Take(5);
    listDelays.OrderByDescending(s => s.count).Take(5).Select((y, index) => {
      barAcumSeries.push({ name: y.code, value: y.count, color: y.color ?? getPalette().list[index]  });
    });

    // fillSeries(barAcumSeries);

    setPieConfigMinutes({
      ...pieConfigMinutes,
      maximum: barSeries.length > 0 ? barSeries[0].value as number : 0,
      series: barSeries
    });

    setPieConfigOccurrences({
      ...pieConfigOccurrences,
      maximum: barAcumSeries.length > 0 ? barAcumSeries[0].value as number : 0,
      series: barAcumSeries
    });

  }, [delays]);


  const handleRefresh = (delayHistoryID: number, _category: ITreeCategoryNode, comment: string, editedBy: string) => {
    setDelays(
      delays.map((delay) => {
        if (delay.delayID === delayHistoryID) {
          // delay.comment = comment;
          return {
            ...delay,
            comment,
            editedBy
          };
        }

        return delay;
      }
      ));

    dispatch(addAlert({ message: "Update was succeeded!", severity: "info", hash: undefined }));
  };

  const handleRefreshAfterCut = (delay: IDelayMeltShop, oldDelayId: number, oldDelayDatetime: number) => {

    const newObject = Object.assign({}, delay);
    const newDelays = [...delays, newObject];

    setDelays(
      newDelays.map((delayNew) => {
        if (delayNew.delayID === oldDelayId) {
          const nDur2 =  oldDelayDatetime - delayNew.beginDatetime ;

          return { ...delayNew, endDatetime:oldDelayDatetime, duration:nDur2 };
        }

        return delayNew;
      }
      ));

    //   dispatch(addAlert({ message: "Update was succeeded!", severity: "info", hash: undefined }));

  };

  const beginShift = delays ? d3.extent(delays, (x) => x.production ? x.production.shiftHistory.beginDatetime : x.beginDatetime) : [];
  const endShift = delays ? d3.extent(delays, (x) => (x.production ? x.production.shiftHistory.endDatetime : x.endDatetime) ?? DateTime.now().toSeconds()) : [];


  const gridStyle = {
    height: parentSize.height,
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto auto 1fr"
  };

  const node = selectedMachine ? selectedNode.children?.find(x => x.code === selectedMachine) : null;
  const showContent = data && delayContext.consolidated && delays.length > 0;

  return (
    <div className="display-grid" style={gridStyle}>
      <FilterComponentBase customFilter={{ "Machine": node ? node.name : "ALL" }} loading={loading}>
        <FormControl className="form-control" >
          <InputLabel id="demo-simple-select-label">Machine</InputLabel>
          <Select
            variant="standard"
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={selectedMachine ?? NullValue}
            displayEmpty={true}
            onChange={(event) => {
              const t = event.target.value;
              setSelectedMachine(t === NullValue ? null : t);
            }}>
            {selectedNode.children && selectedNode.children.map((child) => {
              return <MenuItem key={`child-${child.nodeID}`} value={child.code}>{child.name}</MenuItem>;
            })
            }
            {selectedNode.children && selectedNode.children.length > 1 && <MenuItem value={NullValue}>ALL</MenuItem>}
          </Select>
        </FormControl>
      </FilterComponentBase>
      {loading && <SkeletonView  parentSize={parentSize}/>}
      {error && <Alert severity="warning">{error.message}</Alert>}
      {data && delays.length === 0 &&  <Alert severity="warning">No Data to Display.</Alert>}

      { showContent &&
        <Paper className="mb-1 flex-grow-0 display-flex-column p-1" style={{ position: "relative" }}>
          <div className="display-flex-row align-items-center" style={{ position: "relative", left: 0, top: 0 }}>
            <IconButton onClick={() => setOpen(!open)} size={"small"} className="mr-1">
              {open
                ? <ExpandLessIcon fontSize="inherit" fontWeight="bold"/>
                : <ExpandMoreIcon fontSize="inherit" fontWeight="bold"/>
              }
            </IconButton>
            {!open && <strong>General</strong>}
          </div>

          {open &&
            <>
              <div style={divIndicatorContainer} className="display-flex-row justify-content-center" >
                <div style={divIndicator}>
                  <PieChart pieConfig={pieConfig}/>
                </div>
                <div style={divIndicator}>
                  <PieChart pieConfig={pieConfigMinutes}/>
                </div>
                <div style={divIndicator}>
                  <PieChart pieConfig={pieConfigOccurrences}/>
                </div>
              </div>

              <InterruptionChart beginShift={beginShift[0]  as number} endShift={endShift[1]  as number} delays={delays}
                selected={selected} setSelected={setSelected} title="Delays" machines={selectedNode.children} selectedMachine={selectedMachine}/>
            </>
          }
        </Paper>
      }


      <div className="grid-cell" ref={ref}>
        {showContent && delayContext.consolidated &&
          <InterruptionTable data={delays} selected={selected} setSelected={setSelected}
                              categoryProcessed={delayContext.consolidated}
                              onRefresh={handleRefresh} onRefreshAfterCut={handleRefreshAfterCut}
                              isEditable={isEditable} parentSize={dimensions} />
        }
      </div>
    </div>
  );
};

export default InterruptionPage;
